<script setup lang="ts">
/* BookingList for a company that may sit on either side of a booking: the
   overview and the dashboard mix stays the company booked with stays booked
   at its berths. So each row says who the OTHER party is -- "Booked by X" at
   your berth, the ship for your own booking -- and a request at your berth
   reads "Awaiting you" rather than "Awaiting owner", because you are the
   owner. Otherwise the same row as components/BookingList.vue. */
import { computed } from 'vue'
import Badge from '@/components/ui/Badge.vue'
import ItemList from '@/components/ui/ItemList.vue'
import ItemRow from '@/components/ui/ItemRow.vue'
import { to } from '@/config/routes'
import { useAuth } from '@/lib/auth/useAuth'
import {
  bookingState,
  bookingStateLabel,
  bookingStateTone,
  formatDateTime,
  formatDuration,
  formatEur,
} from '@/lib/bookingLabels'
import type { Booking } from '@/lib/types'

const props = defineProps<{ bookings: Booking[] }>()
const { user } = useAuth()

const rows = computed(() =>
  props.bookings.map((b) => {
    const state = bookingState(b)
    const atMyBerth = b.berth?.company_id === user.value?.companyId
    const mine = b.company_id === user.value?.companyId
    const where = b.berth ? `${b.berth.name}, ${b.berth.city}` : null
    const party =
      atMyBerth && !mine
        ? [b.company ? `Booked by ${b.company.name}` : null, b.vessel?.name].filter(Boolean).join(' · ')
        : (b.vessel?.name ?? null)
    return {
      b,
      state,
      label: state === 'requested' && atMyBerth ? 'Awaiting you' : bookingStateLabel(state),
      line: [where, party].filter(Boolean).join(' · '),
      when: `${formatDateTime(b.arrival)} · ${formatDuration(b.arrival, b.departure)}`,
    }
  }),
)
</script>

<template>
  <ItemList>
    <ItemRow
      v-for="r in rows"
      :key="r.b.id"
      :to="to.booking(r.b.id)"
      :muted="r.state === 'cancelled' || r.state === 'rejected'"
    >
      <template #title>{{ r.b.reference }}</template>
      <template #aside>
        <Badge :tone="bookingStateTone(r.state)">{{ r.label }}</Badge>
      </template>
      <span v-if="r.line">{{ r.line }}</span>
      <span>{{ r.when }} · <strong class="price">{{ formatEur(r.b.price_eur) }}</strong></span>
    </ItemRow>
  </ItemList>
</template>

<style scoped>
.price {
  color: var(--color-heading);
}
</style>
